import { Form } from '$components'
import { z } from 'zod'
import { createContext, useContext, ReactNode } from 'react'
import { EditBusinessDetailsContent } from './content'
import { EditBusinessDetailsControls } from './controls'

export enum Input {
  BusinessName = 'business_name',
  ContactEmail = 'contact_email',
  ContactPhone = 'contact_phone',
}

export const editBusinessDetailsSchema = z.object({
  [Input.BusinessName]: z.string()
    .min(1, { message: 'Business name is required.' })
    .max(255, { message: 'Business name must be less than 255 characters.' }),
  [Input.ContactEmail]: z.string()
    .email({ message: 'Please enter a valid email address.' })
    .max(320, { message: 'Email must be less than 320 characters.' })
    .optional()
    .or(z.literal('')),
  [Input.ContactPhone]: z.string()
    .max(32, { message: 'Phone number must be less than 32 characters.' })
    .optional()
    .or(z.literal('')),
})

export type EditBusinessDetailsSchema = z.infer<typeof editBusinessDetailsSchema>

type EditBusinessDetailsFormContextType = {
  onSubmit: (values: EditBusinessDetailsSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditBusinessDetailsFormContext = createContext<EditBusinessDetailsFormContextType | undefined>(undefined)

type EditBusinessDetailsFormProps = EditBusinessDetailsFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditBusinessDetailsSchema>,
}

export function EditBusinessDetailsForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditBusinessDetailsFormProps) {

  const context: EditBusinessDetailsFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditBusinessDetailsFormContext.Provider value={context}>
      <Form<EditBusinessDetailsSchema, typeof editBusinessDetailsSchema>
        schema={editBusinessDetailsSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditBusinessDetailsFormContext.Provider>
  )
}

export function useEditBusinessDetailsForm() {
  const context = useContext(EditBusinessDetailsFormContext)
  if (context === undefined) {
    throw new Error('useEditBusinessDetailsForm must be used within an EditBusinessDetailsForm')
  }
  return context
}

// Compound components
EditBusinessDetailsForm.Content = EditBusinessDetailsContent
EditBusinessDetailsForm.Controls = EditBusinessDetailsControls
